// Shared helpers to normalize recipe documents into the shape RecipeDetail expects

import { toMinutes } from './recipeTime';

const deriveImageUrl = (raw) => {
  if (!raw) return '';
  if (typeof raw.image === 'string') return raw.image;
  const nested = raw.image?.src || raw.image?.url;
  if (nested) return nested;
  if (Array.isArray(raw.imageUrls) && raw.imageUrls.length) return raw.imageUrls[0];
  return raw.image_url || '';
};

const toLines = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) {
    return val
      .map((x) => (typeof x === 'string' ? x : (x && typeof x === 'object' ? x.text || '' : '')))
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (typeof val === 'string') {
    const trimmed = val.trim();
    const lines = trimmed.includes('\n') ? trimmed.split(/\r?\n/) : trimmed.split(/(?<=\.)\s+/);
    return lines.map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

// Lighter recipe shape for list/card views (Add-to-plan & Swap modals).
// Keeps ingredients/method as raw arrays (no per-line string formatting) — this is
// distinct from normalizeRecipeDetail below, which formats ingredient quantities.
export const normalizeRecipeSummary = (raw) => {
  const imageThumb =
    (typeof raw.imageThumb === 'string' && raw.imageThumb) ||
    (typeof raw.thumbnail === 'string' && raw.thumbnail) ||
    (typeof raw.thumbUrl === 'string' && raw.thumbUrl) ||
    '';
  const image =
    (typeof raw.image === 'string' && raw.image) ||
    raw.image?.src ||
    raw.image?.url ||
    (Array.isArray(raw.imageUrls) && raw.imageUrls[0]) ||
    raw.image_url ||
    raw.photoUrl ||
    '';
  return {
    id: String(raw._id || raw.id || ''),
    name: raw.name || raw.title || 'Untitled',
    image,
    imageThumb,
    // Paths, not just URLs: signed URLs are re-minted from these on read, so a consumer that
    // persists `image` without `imagePath` leaves the two pointing at different recipes.
    imagePath: raw.imagePath || '',
    imageThumbPath: raw.imageThumbPath || raw.thumbnailPath || raw.thumbPath || '',
    prepTime: toMinutes(raw.prepTime),
    cookTime: toMinutes(raw.cookTime),
    totalTime: toMinutes(raw.totalTime),
    mealType: String(raw.mealType || '').toLowerCase(),
    tags: Array.isArray(raw.tags) ? raw.tags : [],
    servings: Number(raw.servings) || Number(raw.serves) || 1,
    ingredients: Array.isArray(raw.ingredients) ? raw.ingredients : [],
    method: Array.isArray(raw.method) ? raw.method : [],
    nutrition: raw.nutrition || {},
    unitSystem: raw.unitSystem || 'metric',
  };
};

export const normalizeRecipeDetail = (raw) => {
  const image = deriveImageUrl(raw);
  const ingredients = Array.isArray(raw.ingredients)
    ? raw.ingredients.map((ing) => {
        const name = ing.name || ing.ingredient || '';
        const unit = ing.unit || ing.units || '';
        const q = ing.quantity;
        let quantityStr = '';
        if (typeof q === 'number') {
          quantityStr = unit ? `${q} ${unit}` : `${q}`;
        } else if (typeof q === 'string') {
          quantityStr = unit ? `${q} ${unit}` : q;
        } else {
          quantityStr = unit ? `${unit}` : '';
        }
        return { name, quantity: quantityStr.trim(), category: ing.category || undefined };
      })
    : [];

  const baseIngredients = Array.isArray(raw.baseIngredients)
    ? raw.baseIngredients.map((ing) => ({
        name: ing.name || ing.ingredient || '',
        unit: ing.unit || ing.units || '',
        quantity: typeof ing.quantity === 'number' ? ing.quantity : Number(ing.quantity) || 0,
        category: ing.category || undefined,
      }))
    : undefined;

  const methodCandidates = [
    raw.method,
    raw.instructions,
    raw.recipeInstructions,
    raw.directions,
    raw.steps,
  ];
  let method = [];
  for (const c of methodCandidates) {
    const lines = toLines(c);
    if (lines.length) {
      method = lines;
      break;
    }
  }

  return {
    id: raw._id || raw.id,
    name: raw.name || raw.title || '',
    image,
    prepTime: toMinutes(raw.prepTime),
    cookTime: toMinutes(raw.cookTime),
    totalTime: toMinutes(raw.totalTime),
    mealType: raw.mealType || '',
    tags: Array.isArray(raw.tags) ? raw.tags : [],
    servings: Number(raw.servings ?? raw.serves) || 1,
    baseServings: Number(raw.baseServings ?? raw.servings ?? raw.serves) || undefined,
    unitSystem: raw.unitSystem || 'metric',
    ingredients,
    baseIngredients,
    method,
    nutrition: raw.nutrition || { calories: 0, protein: 0, carbs: 0, fat: 0 },
  };
};
